import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { sessionAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable/DataTable';
import DateRangeCard from '../../components/DateRangeCard/DateRangeCard';
import { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import { exportRowsToExcel } from '../../utils/excel';
import { guestDetails } from '../../utils/guest';
import AuthMethodBadge, { authMeta } from '../../components/AuthMethodBadge/AuthMethodBadge';
import '../Dashboard/Dashboard.css';
import './SessionReport.css';

const USER_TYPE_OPTIONS = ['guest', 'resident', 'staff', 'visitor', 'otp', 'unknown'];

function toGB(bytes) {
    return Math.round((bytes / 1073741824) * 100) / 100;
}

// The Excel file takes each column's plain field; the screen draws a few of them.
const COLUMNS = [
    { label: 'Sr. No.', field: 'sr' },
    {
        label: 'Guest', field: 'username', filter: true,
        render: (r) => (
            <span className="td-guest">
                <strong>{r.username}</strong>
                {r.details && <small>{r.details}</small>}
            </span>
        ),
        searchValue: (r) => `${r.username} ${r.details}`,
    },
    { label: 'Room', field: 'roomNumber', filter: true },
    { label: 'Type', field: 'userType', filter: { options: USER_TYPE_OPTIONS } },
    {
        // How the guest signed in (PMS, voucher, SMS / WhatsApp OTP, …) — the Excel file gets the names.
        label: 'Auth', field: 'authLabel', filter: true,
        render: (r) => (r.authMethods.length
            ? <span className="sr-auth">{r.authMethods.map((m) => <AuthMethodBadge key={m} method={m} small />)}</span>
            : <span className="sr-muted">—</span>),
    },
    { label: 'Sessions', field: 'sessions' },
    { label: 'Devices', field: 'uniqueMacs' },
    {
        label: 'Usage (GB)', field: 'usageGb',
        render: (r) => (
            <span className="sr-usage" title={`${r.sharePct}% of the usage in this range`}>
                <span className="td-meter sr-meter" aria-hidden="true"><span className="td-meter-fill" style={{ width: `${r.share}%` }}></span></span>
                <strong>{r.usageGb}</strong>
                <small>{r.sharePct}% of total</small>
            </span>
        ),
    },
    { label: 'Upload (GB)', field: 'uploadGb' },
    { label: 'Download (GB)', field: 'downloadGb' },
];

const SessionReport = () => {
    const { selectedBranch } = useBranchStore();
    const [range, setRange] = useState({ start: '', end: '' });
    const [userType, setUserType] = useState('');
    const [report, setReport] = useState({ rows: [], totals: { sessions: 0, usageBytes: 0, uploadBytes: 0, downloadBytes: 0, uniqueMacs: 0 } });
    const [loading, setLoading] = useState(true);

    const fetchReport = useCallback(async () => {
        try {
            setLoading(true);
            // The date and time picked, as ISO (the server has no idea of India time).
            const { from, to } = rangeParams({ from: range.start, to: range.end });
            const params = { branchId: selectedBranch?._id };
            if (from) params.from = from;
            if (to) params.to = to;
            if (userType) params.userType = userType;
            const res = await sessionAPI.getReport(params);
            setReport(res.data.data || { rows: [], totals: {} });
        } catch (error) {
            toast.error('Failed to fetch session report');
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, range, userType]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const totals = report.totals || {};

    // Who each row is comes from the report (the guest's name, else their room, else their
    // mobile); the meter is against the heaviest user, the share against everyone's total.
    const rows = useMemo(() => {
        const list = report.rows || [];
        const top = Math.max(0, ...list.map((r) => r.usageBytes || 0));
        return list.map((r, i) => ({
            id: r.userId,
            sr: i + 1,
            username: r.username,
            details: guestDetails(r.guest).join(' · '),
            roomNumber: r.guest?.roomNumber || '',
            userType: r.userType,
            authMethods: r.authMethods || [],
            authLabel: (r.authMethods || []).map((m) => authMeta(m).label).join(', '),
            sessions: r.sessions,
            uniqueMacs: r.uniqueMacs,
            usageGb: toGB(r.usageBytes),
            uploadGb: toGB(r.uploadBytes),
            downloadGb: toGB(r.downloadBytes),
            share: top ? Math.round(((r.usageBytes || 0) / top) * 100) : 0,
            sharePct: totals.usageBytes ? Math.round(((r.usageBytes || 0) / totals.usageBytes) * 100) : 0,
        }));
    }, [report.rows, totals.usageBytes]);

    // The guest's details (name / room / mobile) get a column of their own; the file
    // numbers its rows itself (Sr. No.).
    function handleExportExcel() {
        const [, guest, ...rest] = COLUMNS;
        const day = (value) => (value ? String(value).slice(0, 10) : '');
        exportRowsToExcel(`session-report-${day(range.start) || 'all'}-to-${day(range.end) || 'now'}`,
            [guest, { label: 'Details', field: 'details' }, ...rest], rows, { sheetName: 'Session Report' });
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-chart-line"></i>
                            Session Dashboard
                        </h1>
                        <p className="page-subtitle-modern">Internet usage — sessions, devices and data per guest</p>
                    </div>
                </div>
            </div>

            <DateRangeCard withTime value={range} onApply={setRange} onClear={() => setRange({ start: '', end: '' })} />

            <div className="ad-stats-grid sr-stats" style={{ marginTop: '1.5rem' }}>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip ad-strip-purple"></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon ad-icon-purple"><i className="fas fa-wifi"></i></div>
                        <span className="ad-stat-label">Total Sessions</span>
                    </div>
                    <div className="ad-stat-value">{(totals.sessions || 0).toLocaleString()}</div>
                </div>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip ad-strip-blue"></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon ad-icon-blue"><i className="fas fa-database"></i></div>
                        <span className="ad-stat-label">Total Usage</span>
                    </div>
                    <div className="ad-stat-value">{toGB(totals.usageBytes || 0)} GB</div>
                </div>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip ad-strip-green"></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon ad-icon-green"><i className="fas fa-arrow-up"></i></div>
                        <span className="ad-stat-label">Upload</span>
                    </div>
                    <div className="ad-stat-value">{toGB(totals.uploadBytes || 0)} GB</div>
                </div>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip" style={{ background: '#f59e0b' }}></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon" style={{ background: '#f59e0b18', color: '#f59e0b' }}><i className="fas fa-arrow-down"></i></div>
                        <span className="ad-stat-label">Download</span>
                    </div>
                    <div className="ad-stat-value">{toGB(totals.downloadBytes || 0)} GB</div>
                </div>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip" style={{ background: '#0ea5e9' }}></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon" style={{ background: '#0ea5e918', color: '#0ea5e9' }}><i className="fas fa-laptop"></i></div>
                        <span className="ad-stat-label">Unique Devices</span>
                    </div>
                    <div className="ad-stat-value">{(totals.uniqueMacs || 0).toLocaleString()}</div>
                </div>
            </div>

            <div className="ad-card" style={{ marginTop: '1.5rem' }}>
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-users"></i>
                        <h2>Per-guest usage</h2>
                        {!loading && <span className="sr-count">{rows.length} {rows.length === 1 ? 'guest' : 'guests'}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center' }}>
                        <select
                            className="filter-select-modern"
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                        >
                            <option value="">All user types</option>
                            {USER_TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                        <button type="button" className="btn-secondary-modern btn-sm" onClick={handleExportExcel}>
                            <i className="fas fa-file-excel"></i> Excel
                        </button>
                        <button type="button" className="btn-secondary-modern btn-sm" onClick={() => window.print()}>
                            <i className="fas fa-print"></i> Print
                        </button>
                    </div>
                </div>
                <div style={{ padding: '0 1.375rem 1.375rem' }}>
                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div><p>Loading session report...</p></div>
                    ) : (
                        <DataTable
                            defaultPageSize={25}
                            columns={COLUMNS}
                            rows={rows}
                            emptyMessage="No sessions found for the selected range"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SessionReport;
